import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface BakimliMusteri {
  id: string;
  musteri_id: string;
  ad: string;
  soyad: string;
  telefon: string;
  il: string;
  ilce: string;
  acik_adres: string;
  cihaz_turu: string;
  son_bakim_tarihi: string | null;
  bakim_periyodu_ay: number;
  bir_sonraki_bakim_tarihi: string | null;
  hatirlatma_durumu: string;
  son_iletisim_tarihi: string | null;
  sonuc: string;
  notlar: string;
  toplam_harcama: number;
}

export function useBakimTakip() {
  const [musteriler, setMusteriler] = useState<BakimliMusteri[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all active customers
      const { data: musteriData, error: mErr } = await supabase
        .from('sadem_musteriler')
        .select('id, ad, soyad, telefon, il, ilce, acik_adres')
        .eq('aktif', true)
        .eq('silindi', false)
        .order('ad');

      if (mErr) throw mErr;

      // Get latest bakım record per customer
      const { data: bakimData, error: bErr } = await supabase
        .from('sadem_bakim_takibi')
        .select('*')
        .eq('aktif', true)
        .order('son_bakim_tarihi', { ascending: false });

      if (bErr) throw bErr;

      // Get cihaz info
      const { data: cihazData, error: cErr } = await supabase
        .from('sadem_cihazlar')
        .select('musteri_id, cihaz_turu')
        .eq('aktif', true);

      if (cErr) throw cErr;

      // Get toplam harcama
      const { data: odemeData, error: oErr } = await supabase
        .from('sadem_odemeler')
        .select('musteri_id, tutar')
        .eq('aktif', true)
        .eq('odeme_durumu', 'odendi');

      if (oErr) throw oErr;

      // Build map: musteri_id → latest bakım record
      const bakimMap: Record<string, any> = {};
      (bakimData || []).forEach((b: any) => {
        if (!bakimMap[b.musteri_id]) bakimMap[b.musteri_id] = b;
      });

      const cihazMap: Record<string, string> = {};
      (cihazData || []).forEach((c: any) => {
        if (!cihazMap[c.musteri_id]) cihazMap[c.musteri_id] = c.cihaz_turu;
      });

      const harcamaMap: Record<string, number> = {};
      (odemeData || []).forEach((o: any) => {
        harcamaMap[o.musteri_id] = (harcamaMap[o.musteri_id] || 0) + Number(o.tutar);
      });

      const mapped: BakimliMusteri[] = (musteriData || []).map((m: any) => {
        const bakim = bakimMap[m.id];
        return {
          id: m.id,
          musteri_id: m.id,
          ad: m.ad,
          soyad: m.soyad,
          telefon: m.telefon,
          il: m.il,
          ilce: m.ilce,
          acik_adres: m.acik_adres,
          cihaz_turu: cihazMap[m.id] || 'Belirtilmemiş',
          son_bakim_tarihi: bakim?.son_bakim_tarihi || null,
          bakim_periyodu_ay: bakim?.bakim_periyodu_ay || 6,
          bir_sonraki_bakim_tarihi: bakim?.bir_sonraki_bakim_tarihi || null,
          hatirlatma_durumu: bakim?.hatirlatma_durumu || 'beklemede',
          son_iletisim_tarihi: bakim?.son_iletisim_tarihi || null,
          sonuc: bakim?.sonuc || '',
          notlar: bakim?.notlar || '',
          toplam_harcama: harcamaMap[m.id] || 0,
        };
      });

      setMusteriler(mapped);
    } catch (err: any) {
      setError(err.message || 'Veriler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { musteriler, loading, error, refetch: fetchData };
}